import React, { FC, useEffect, useState } from 'react';
import { Badge, Avatar, Dropdown, Space, Button, Flex } from 'antd';
import { Gift, HelpCircle } from 'lucide-react';
import styles from './index.module.css';
import * as translations from './strings';
import { getLangKey } from 'appJS/models/Language';
import { Endpoints, User } from './types';
import cx from 'classnames';
import { rightNav } from './getItems';
import { subscribeToNewMessageEvent } from 'appJS/utils/HelpArticleButton';

const strings = translations[getLangKey()];

interface Props {
  user: User;
  isActiveOrganization: boolean;
  endpoints: Endpoints;
  isMobileView: boolean;
  container: HTMLDivElement;
  setIsModalOpen: (boolean) => void;
  isModalOpen: boolean;
  toggleMobileMenu: () => void;
  toggleDarkMode: () => void;
  darkThemeIcon: React.ReactNode;
  darkModeString: string;
}

export const RightNav: FC<Props> = Props => {
  const {
    user: {
      id,
      isImpersonating,
      daysLeftInTrial,
      genericAvatar,
      isGuest,
      isCollaborater,
      organization,
      amountOfTrialDays,
      membership,
      accessTo,
      name,
      new_avatar_url,
      admin,
      surname,
      email
    },
    endpoints: { stopImpersonsating, adminUrl, subscription, homeUrl },
    isActiveOrganization,
    container,
    isMobileView,
    setIsModalOpen,
    toggleDarkMode,
    darkModeString,
    darkThemeIcon,
    toggleMobileMenu
  } = Props;
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const inTrial = organization?.in_trial;
  const userFirstNameFirstLetter = (name || email)?.[0];
  const emailFirstLetter = email?.[0];
  const userSurnameFirstLetter = surname?.[0];
  const userAvatar = new_avatar_url;
  const isMember = membership?.role === 'member';
  const showTrialMessage =
    !isMobileView && organization && accessTo?.billing && inTrial;

  const userClassName = (): string => {
    if (isGuest) {
      return 'guest-class';
    } else if (isCollaborater) {
      return 'collab-class';
    } else {
      return 'member-class';
    }
  };

  const items = rightNav({
    setIsModalOpen,
    adminUrl,
    admin,
    isGuest,
    isMember,
    daysLeftInTrial,
    amountOfTrialDays,
    id,
    isActiveOrganization,
    homeUrl,
    toggleDarkMode,
    darkModeString,
    darkThemeIcon,
    unreadCount
  });

  useEffect(() => {
    subscribeToNewMessageEvent(setUnreadCount);
  }, []);

  return (
    <Flex gap="small">
      {isImpersonating && (
        <Flex gap="small" align="center">
          <span>** Impersonating User! **</span>
          <a href={stopImpersonsating}> Back to Admin</a>
        </Flex>
      )}

      {showTrialMessage && (
        <span className={styles.upgradeWrapper}>
          <p>
            {daysLeftInTrial} {strings.remaining}
          </p>

          <Button type="link" href={subscription}>
            {' '}
            {strings.upgradeNow}
          </Button>
        </span>
      )}
      <div
        className={cx({
          [styles.greyWrapper]: !isMobileView
        })}
      >
        {!isMobileView && (
          <>
            <a onClick={e => e.preventDefault} id="whats-new">
              <Gift size={24} />
            </a>
            <Badge count={unreadCount} overflowCount={99} size="small">
              <a
                className="open_intercom_chat"
                onClick={e => {
                  e.preventDefault();
                  if (window.Intercom) {
                    window.Intercom('showSpace', 'home');
                  }
                }}
              >
                {' '}
                <HelpCircle size={24} />
              </a>
            </Badge>
          </>
        )}

        <div onClick={toggleMobileMenu}>
          <Dropdown
            getPopupContainer={() => container}
            disabled={isMobileView}
            overlayClassName={styles.overlay}
            className={styles.navbarMenu}
            menu={{ items }}
            trigger={['click']}
            placement="bottomRight"
          >
            <a onClick={e => e.preventDefault()}>
              <Space>
                <div>
                  <div className={cx({ 'avatar-wrap': !genericAvatar })}>
                    <Avatar
                      className={userClassName()}
                      size={!genericAvatar ? 'large' : undefined}
                      src={genericAvatar ? userAvatar : undefined}
                      crossOrigin="anonymous"
                    >
                      {userFirstNameFirstLetter || emailFirstLetter}
                      {userFirstNameFirstLetter && userSurnameFirstLetter}
                    </Avatar>
                  </div>
                </div>
              </Space>
            </a>
          </Dropdown>
        </div>
      </div>
    </Flex>
  );
};
